import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Server, Trash2 } from "lucide-react";

import { api, ApiError, type Nvr } from "../../api";
import { Modal } from "../../components/Modal";
import { useScope } from "../../components/ScopeContext";
import {
  CustomerRecorderDialog,
  editRecorderDraft,
  newRecorderDraft,
  type RecorderDraft,
} from "./CustomerRecorderDialog";
import { BRAND_OPTIONS, EmptyState, Note, SettingsSection } from "./shared";

/** A customer's video recorders, each connected through one of their boxes. */
export function CustomerRecordersSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { scope, stores } = useScope();
  const list = useQuery({ queryKey: ["nvrs"], queryFn: api.nvrs.list });
  const boxes = useQuery({ queryKey: ["connectors"], queryFn: api.connectors.list });
  const cameras = useQuery({ queryKey: ["cameras", "all"], queryFn: () => api.cameras.list() });
  const [draft, setDraft] = useState<RecorderDraft | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Nvr | null>(null);
  const [error, setError] = useState<string | null>(null);

  const del = useMutation({
    mutationFn: api.nvrs.delete,
    onSuccess: () => {
      setConfirmDelete(null);
      setError(null);
      qc.invalidateQueries({ queryKey: ["nvrs"] });
      qc.invalidateQueries({ queryKey: ["connectors"] });
      qc.invalidateQueries({ queryKey: ["stores"] });
    },
    onError: (e: unknown) => {
      setConfirmDelete(null);
      setError(e instanceof ApiError ? e.message : "Could not remove the video recorder.");
    },
  });

  const scoped = scope.kind === "store" ? scope.store : "";
  const visible = useMemo(
    () => (list.data ?? []).filter((n) => !scoped || n.store === scoped),
    [list.data, scoped],
  );
  const storeLabel = (name?: string) =>
    stores.find((s) => s.name === name)?.display_name || name || "";
  const boxLabel = (id?: string | null) => {
    const b = (boxes.data ?? []).find((x) => x.id === id);
    return b ? (b.name && b.name !== b.serial ? b.name : `IntraScene box ${b.serial}`) : "";
  };
  const camerasOn = (id: string) =>
    (cameras.data ?? []).filter((c) => c.nvr_id === id).length;
  const brandLabel = (id: string) => BRAND_OPTIONS.find((b) => b.id === id)?.label ?? id;
  const add = () => setDraft(newRecorderDraft({ store: scoped || stores[0]?.name || "" }));

  return (
    <SettingsSection
      title="Video recorders"
      description="The recorders your cameras are plugged into."
      hint={
        <>
          Your IntraScene box finds the video recorders on its network. Add one
          with the username and password you use to sign in to it, then add its
          cameras by channel number.
        </>
      }
      actions={
        canEdit && (
          <button
            className="bf-button disabled:opacity-40"
            onClick={add}
            disabled={stores.length === 0}
            title={stores.length === 0 ? "Add your IntraScene box first" : undefined}
          >
            <Plus size={16} className="mr-2" /> Add video recorder
          </button>
        )
      }
    >
      {error && <Note tone="error">{error}</Note>}

      {visible.length === 0 ? (
        <EmptyState
          icon={Server}
          title="No video recorders yet"
          hint="Add your IntraScene box first; it lists the video recorders it can see."
        />
      ) : (
        <div className="bf-card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Location</th>
                <th className="p-3">Connected through</th>
                <th className="p-3">Cameras</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((n) => (
                <tr key={n.id} className="border-t border-white/5">
                  <td className="p-3">
                    <div className="font-medium">{n.name || brandLabel(n.brand)}</div>
                    <div className="text-xs text-bf-muted">{brandLabel(n.brand)}</div>
                  </td>
                  <td className="p-3">{storeLabel(n.store)}</td>
                  <td className="p-3 text-bf-muted">{boxLabel(n.connector_id)}</td>
                  <td className="p-3 tabular-nums text-bf-muted">{camerasOn(n.id)}</td>
                  <td className="p-3 text-right">
                    {canEdit && (
                      <div className="flex justify-end gap-3">
                        <button
                          className="text-bf-muted hover:text-bf-text"
                          aria-label={`Edit ${n.name || n.id}`}
                          onClick={() => setDraft(editRecorderDraft(n))}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          className="text-bf-muted hover:text-red-400"
                          aria-label={`Remove ${n.name || n.id}`}
                          onClick={() => setConfirmDelete(n)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {draft && <CustomerRecorderDialog draft={draft} onClose={() => setDraft(null)} />}

      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(null)} panelClassName="w-full max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">
              Remove “{confirmDelete.name || brandLabel(confirmDelete.brand)}”?
            </h3>
            <p className="text-sm text-bf-muted">
              {camerasOn(confirmDelete.id) > 0
                ? "Remove its cameras first."
                : "It stops showing in IntraScene. Nothing on the recorder itself is changed."}
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="bf-button bg-white/10 bg-none"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className="bf-button bg-red-600 bg-none hover:bg-red-500 disabled:opacity-40"
                disabled={del.isPending || camerasOn(confirmDelete.id) > 0}
                onClick={() => del.mutate(confirmDelete.id)}
              >
                {del.isPending ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </SettingsSection>
  );
}
